import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { format, differenceInDays } from 'date-fns'
import type { Profile } from '@/types/database'

export default function AdminBillingPage() {
  const [stats, setStats] = useState({
    active: 0,
    trialing: 0,
    pastDue: 0,
    canceled: 0,
    mrr: 0,
  })
  const [pastDueUsers, setPastDueUsers] = useState<Profile[]>([])
  const [expiringTrials, setExpiringTrials] = useState<Profile[]>([])
  const [expiredTrials, setExpiredTrials] = useState<Profile[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchBillingData()
  }, [])

  const fetchBillingData = async () => {
    setIsLoading(true)

    try {
      const { data: profiles } = await supabase.from('profiles').select('*') as { data: Profile[] | null }

      if (profiles) {
        const statusCounts = profiles.reduce((acc, p) => {
          acc[p.subscription_status] = (acc[p.subscription_status] || 0) + 1
          return acc
        }, {} as Record<string, number>)

        setStats({
          active: statusCounts['active'] || 0,
          trialing: statusCounts['trialing'] || 0,
          pastDue: statusCounts['past_due'] || 0,
          canceled: statusCounts['canceled'] || 0,
          mrr: (statusCounts['active'] || 0) * 10,
        })

        // Past due users
        setPastDueUsers(profiles.filter(p => p.subscription_status === 'past_due'))

        // Trials expiring in 3 days
        const now = new Date()
        setExpiringTrials(
          profiles.filter(p => {
            if (p.subscription_status !== 'trialing' || !p.trial_ends_at) return false
            const daysLeft = differenceInDays(new Date(p.trial_ends_at), now)
            return daysLeft >= 0 && daysLeft <= 3
          })
        )

        // Expired trials (trialing but trial_ends_at is past)
        setExpiredTrials(
          profiles.filter(p => {
            if (p.subscription_status !== 'trialing' || !p.trial_ends_at) return false
            return new Date(p.trial_ends_at) < now
          })
        )
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Billing Monitor</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">Active</p>
          <p className="text-2xl font-bold text-green-400">{stats.active}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">Trialing</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.trialing}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">Past Due</p>
          <p className="text-2xl font-bold text-red-400">{stats.pastDue}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">Canceled</p>
          <p className="text-2xl font-bold text-gray-400">{stats.canceled}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-sm text-gray-400">MRR</p>
          <p className="text-2xl font-bold text-green-400">€{stats.mrr}</p>
        </div>
      </div>

      {/* Warnings */}
      {expiredTrials.length > 0 && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 mb-6">
          <p className="text-red-300 font-medium">
            {expiredTrials.length} user(s) have expired trials but are still marked as trialing
          </p>
        </div>
      )}

      {pastDueUsers.length > 0 && (
        <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-xl p-4 mb-6">
          <p className="text-yellow-300 font-medium">
            {pastDueUsers.length} user(s) have failed payments
          </p>
        </div>
      )}

      {/* Past Due Users */}
      {pastDueUsers.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Failed Payments</h2>
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Stripe ID</th>
                  <th className="px-4 py-3 font-medium">Period End</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {pastDueUsers.map((user) => (
                  <tr key={user.id} className="border-b border-gray-700">
                    <td className="px-4 py-3 text-white">{user.email}</td>
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                      {user.stripe_customer_id || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {user.current_period_end
                        ? format(new Date(user.current_period_end), 'MMM d, yyyy')
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/users/${user.id}`}
                        className="text-primary-400 hover:text-primary-300"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Expiring Trials */}
      {expiringTrials.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-white mb-4">
            Trials Expiring Soon ({expiringTrials.length})
          </h2>
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Trial Ends</th>
                  <th className="px-4 py-3 font-medium">Days Left</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {expiringTrials.map((user) => {
                  const daysLeft = user.trial_ends_at
                    ? differenceInDays(new Date(user.trial_ends_at), new Date())
                    : 0

                  return (
                    <tr key={user.id} className="border-b border-gray-700">
                      <td className="px-4 py-3 text-white">{user.email}</td>
                      <td className="px-4 py-3 text-gray-400">
                        {user.trial_ends_at
                          ? format(new Date(user.trial_ends_at), 'MMM d, yyyy')
                          : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${daysLeft <= 1 ? 'badge-danger' : 'badge-warning'}`}>
                          {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/users/${user.id}`}
                          className="text-primary-400 hover:text-primary-300"
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {pastDueUsers.length === 0 && expiringTrials.length === 0 && (
        <div className="bg-gray-800 rounded-xl p-8 text-center">
          <p className="text-green-400 font-medium mb-2">All clear!</p>
          <p className="text-gray-400">No billing issues to review</p>
        </div>
      )}
    </div>
  )
}
